import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { adminGet, adminPost } from "../services/api";
import {
  ErrorCircleRegular, ArrowClockwiseRegular, ArrowSyncRegular, FilterRegular, SparkleRegular, BrainRegular, ServerRegular,
  PhoneRegular, GlobeRegular, ShieldErrorRegular, DatabaseRegular, TranslateRegular, AlertRegular,
} from "@fluentui/react-icons";
import { fluentIcon } from "../lib/fluentIcon";

const AlertCircle = fluentIcon(ErrorCircleRegular);
const RefreshCw = fluentIcon(ArrowClockwiseRegular);
const Loader2 = fluentIcon(ArrowSyncRegular);
const Filter = fluentIcon(FilterRegular);
const Sparkles = fluentIcon(SparkleRegular);
const Brain = fluentIcon(BrainRegular);
const Server = fluentIcon(ServerRegular);
const Smartphone = fluentIcon(PhoneRegular);
const Globe = fluentIcon(GlobeRegular);
const ShieldAlert = fluentIcon(ShieldErrorRegular);
const Database = fluentIcon(DatabaseRegular);
const Languages = fluentIcon(TranslateRegular);
const Bell = fluentIcon(AlertRegular);

interface ErrorLog {
  id: string;
  surface: string;
  error_msg: string;
  article_id: string | null;
  user_id: string | null;
  providers: { name: string; error: string }[] | null;
  context: Record<string, any> | null;
  created_at: string;
}

interface ErrorLogsResponse {
  status: string;
  data: ErrorLog[];
  total: number;
}

const CATEGORY_ICONS: Record<string, any> = {
  ai: Brain, api: Server, auth: ShieldAlert, mobile: Smartphone,
  web: Globe, ingestion: Database, translation: Languages, push: Bell, unknown: AlertCircle,
};

const CATEGORY_COLORS: Record<string, string> = {
  ai: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  api: "bg-brand-blue/20 text-brand-blue border-brand-blue/30",
  auth: "bg-red-500/20 text-red-400 border-red-500/30",
  ingestion: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  translation: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  mobile: "bg-pink-500/20 text-pink-400 border-pink-500/30",
  push: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  unknown: "bg-slate-500/20 text-slate-400 border-slate-500/30",
};

export default function ErrorsPage() {
  const { token } = useAuth();
  const [filter, setFilter] = useState<string>("all");
  interface ErrorInsight {
    summary: string;
    patterns: { cause: string; count: string; fix: string }[];
    critical: string[];
  }
  const [aiAnalysis, setAiAnalysis] = useState<ErrorInsight | null>(null);
  const [aiRaw, setAiRaw] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHidden, setAiHidden] = useState(false);

  const { data, isLoading, error, refetch } = useQuery<ErrorLogsResponse>({
    queryKey: ["error-logs"],
    queryFn: () => adminGet<ErrorLogsResponse>("/api/v1/admin/error-logs?page=1&limit=50", token),
    refetchInterval: 60_000,
  });

  const errors = data?.data ?? [];
  const filtered = filter === "all" ? errors : errors.filter(e => {
    const cat = (e.context as any)?.category ?? "unknown";
    return cat === filter || e.surface === filter;
  });

  // Count by category
  const categoryCounts: Record<string, number> = {};
  for (const e of errors) {
    const cat = (e.context as any)?.category ?? e.surface;
    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
  }

  const analyzeErrors = async () => {
    setAiLoading(true);
    setAiAnalysis(null);
    setAiRaw(null);
    setAiHidden(false);
    try {
      const errorSummary = errors.slice(0, 20).map(e => {
        const cat = (e.context as any)?.category ?? e.surface;
        const endpoint = (e.context as any)?.endpoint ?? "";
        return `[${cat}] ${e.error_msg.slice(0, 100)} ${endpoint ? `(${endpoint})` : ""}`;
      }).join("\n");

      const userContent = `${errors.length} errors total (top 20):\n\n${errorSummary}\n\nReturn JSON.`;
      const d = await adminPost<{ content: string }>("/api/v1/admin/ai-analyze", { kind: "errors", context: userContent }, token);
      const content = d.content ?? "";
      try { setAiAnalysis(JSON.parse(content)); } catch { setAiRaw(content || "No response"); }
    } catch (e: any) {
      setAiRaw(`Error: ${e.message}`);
    }
    setAiLoading(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Error Logs</h1>
          <p className="text-sm text-slate-500 mt-1">All Tomitra errors: AI, API, ingestion, auth, mobile, translation. Refreshes every 60s.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={analyzeErrors} disabled={aiLoading || errors.length === 0}
            className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 rounded-lg text-xs font-medium transition-colors">
            {aiLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI Analyze
          </button>
          <button onClick={() => refetch()} disabled={isLoading}
            className="flex items-center gap-2 px-3 py-2 bg-slate-900 border border-slate-800 hover:bg-slate-800 rounded-lg text-xs transition-colors">
            {isLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          </button>
        </div>
      </div>

      {/* Show button when hidden */}
      {aiHidden && (aiAnalysis || aiRaw) && (
        <button onClick={() => setAiHidden(false)}
          className="flex items-center gap-2 mb-4 px-3 py-2 bg-purple-950/20 border border-purple-900/30 rounded-lg text-xs text-purple-400 hover:bg-purple-900/20 transition-colors w-full justify-center">
          <Sparkles size={12} /> Show Error Analysis
        </button>
      )}

      {/* AI Analysis Panel */}
      {!aiHidden && (aiAnalysis || aiRaw || aiLoading) && (
        <div className="bg-purple-950/20 border border-purple-900/30 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-purple-400" />
              <h2 className="text-sm font-semibold text-purple-300">Gemini Error Analysis</h2>
              <span className="text-[10px] text-slate-500 bg-slate-800/50 px-2 py-0.5 rounded-full">Last 24 hours | Top 20 errors</span>
            </div>
            <button onClick={() => setAiHidden(true)} className="text-slate-500 hover:text-slate-300 text-xs px-2 py-1 border border-slate-700 rounded-md hover:border-slate-500">Hide</button>
          </div>
          {aiLoading ? (
            <div className="flex items-center gap-2 text-slate-500 py-2"><Loader2 size={14} className="animate-spin" /> Analyzing error patterns...</div>
          ) : aiAnalysis ? (
            <div>
              <p className="text-sm text-slate-300 mb-4 leading-relaxed">{aiAnalysis.summary}</p>
              {aiAnalysis.patterns && aiAnalysis.patterns.length > 0 && (
                <div className="mb-4">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Error Patterns</div>
                  <div className="space-y-2">
                    {aiAnalysis.patterns.map((p, i) => (
                      <div key={i} className="bg-slate-800/40 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-slate-200">{p.cause}</span>
                          <span className="text-[10px] font-mono text-red-400 bg-red-500/10 px-2 py-0.5 rounded-full">{p.count}</span>
                        </div>
                        <p className="text-[10px] text-emerald-400">Fix: {p.fix}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {aiAnalysis.critical && aiAnalysis.critical.length > 0 && (
                <div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Critical Issues</div>
                  {aiAnalysis.critical.map((c, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-red-400 mb-1">
                      <AlertCircle size={10} className="mt-0.5 flex-shrink-0" /> <span className="text-slate-400">{c}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : aiRaw ? (
            <div className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap">{aiRaw}</div>
          ) : null}
        </div>
      )}

      {error && <div className="bg-red-950/30 border border-red-900/40 rounded-xl p-4 text-red-400 text-sm mb-4">{(error as Error).message}</div>}

      {data && (
        <>
          {/* Category filter pills */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Filter size={12} className="text-slate-500" />
            <button onClick={() => setFilter("all")}
              className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${filter === "all" ? "bg-brand-blue/20 text-brand-blue border-brand-blue/30" : "bg-slate-800/50 text-slate-500 border-slate-700 hover:text-slate-300"}`}>
              All ({data.total})
            </button>
            {Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]).map(([cat, count]) => {
              const color = CATEGORY_COLORS[cat] ?? CATEGORY_COLORS.unknown;
              return (
                <button key={cat} onClick={() => setFilter(cat)}
                  className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${filter === cat ? color : "bg-slate-800/50 text-slate-500 border-slate-700 hover:text-slate-300"}`}>
                  {cat} ({count})
                </button>
              );
            })}
          </div>

          {/* Error list */}
          <div className="space-y-2">
            {filtered.length === 0 && (
              <div className="bg-emerald-950/20 border border-emerald-900/30 rounded-xl p-8 text-center">
                <CheckCircle size={24} className="text-emerald-400 mx-auto mb-2" />
                <div className="text-sm text-emerald-400 font-medium">No errors{filter !== "all" ? ` in "${filter}"` : ""}. Everything healthy.</div>
              </div>
            )}
            {filtered.map(e => {
              const cat = (e.context as any)?.category ?? e.surface;
              const CatIcon = CATEGORY_ICONS[cat] ?? AlertCircle;
              const catColor = CATEGORY_COLORS[cat] ?? CATEGORY_COLORS.unknown;
              const endpoint = (e.context as any)?.endpoint;
              const statusCode = (e.context as any)?.status_code;
              const stack = (e.context as any)?.stack;

              return (
                <div key={e.id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 hover:border-slate-700 transition-colors">
                  <div className="flex items-start gap-3">
                    <CatIcon size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[10px] mb-1.5 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${catColor}`}>{cat}</span>
                        <span className="text-slate-500 px-1.5 py-0.5 bg-slate-800 rounded">{e.surface}</span>
                        <span className="text-slate-500">{new Date(e.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
                        {statusCode && <span className="text-red-400 font-mono">{statusCode}</span>}
                        {endpoint && <span className="text-slate-500 font-mono">{endpoint}</span>}
                      </div>
                      <div className="text-xs text-slate-200 font-medium mb-1 break-words">{e.error_msg}</div>
                      {e.providers && e.providers.length > 0 && (
                        <div className="mt-2 space-y-0.5">
                          {e.providers.map((p, i) => (
                            <div key={i} className="text-[10px] font-mono text-slate-500">
                              <span className="text-slate-400">{p.name}</span>: {p.error}
                            </div>
                          ))}
                        </div>
                      )}
                      {stack && (
                        <details className="mt-2">
                          <summary className="text-[10px] text-slate-600 cursor-pointer hover:text-slate-400">Stack trace</summary>
                          <pre className="text-[10px] text-slate-600 font-mono mt-1 whitespace-pre-wrap">{stack}</pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function CheckCircle({ size, className }: { size: number; className: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
}
